<!-- ==================================== POPUP : DICTATION / AI (STARTS) ============================================== -->
<div class="t-popup popup-auto-text st-popup" style="display: block">
  <div class="popup-close"></div>

  <div class="popup-frame st-frame">
    <div class="popup-body st-body">
      <header class="st-header">
        <h2>Consultation dictation</h2>
        <button
          type="button"
          class="popup-close-button"
          (click)="closed.emit()"
          [title]="'GUI.PrintOpts.Close Printout' | translate"
        ></button>
      </header>

      <div class="st-message st-message--warn" *ngIf="notEnabled">
        Speech to text is not enabled for this clinic or user. Please contact support to enable it.
      </div>
      <div class="st-message st-message--warn" *ngIf="!speechSupported && !readOnly">
        This browser does not support dictation. Please use Google Chrome or Microsoft Edge.
      </div>

      <!-- closed or read-only exam: what was dictated and asked, nothing that changes the exam -->
      <ng-container *ngIf="!notEnabled && readOnly">
        <div class="st-message st-message--warn">This exam is closed. The transcript is shown for viewing only.</div>
        <section class="st-section">
          <ng-container *ngIf="historyLoaded && !transcript">
            <p class="st-hint">No transcript was saved for this exam.</p>
          </ng-container>
          <ng-container *ngIf="transcript">
            <label class="st-label" for="st-transcript-view">Transcript</label>
            <textarea
              id="st-transcript-view"
              class="st-textarea st-transcript"
              [value]="transcript"
              readonly
            ></textarea>
          </ng-container>
          <ng-container *ngIf="instructions">
            <div class="st-label">Special instructions for the AI</div>
            <p class="st-readonly-text">{{ instructions }}</p>
          </ng-container>
          <ng-container *ngIf="history.length">
            <div class="st-label">Request history</div>
            <ul class="st-history">
              <li *ngFor="let h of history">
                <strong>{{ h.time | date: 'dd/MM/yy HH:mm' }} – {{ h.type }}</strong>
                <span *ngIf="h.text">: {{ h.text }}</span>
              </li>
            </ul>
          </ng-container>
        </section>
      </ng-container>

      <ng-container *ngIf="!notEnabled && !readOnly">
        <!-- 1. recording -->
        <section class="st-section">
          <div class="st-controls">
            <select
              class="t-select st-language"
              [(ngModel)]="languageSelect"
              (ngModelChange)="onLanguageChange()"
              aria-label="Dictation language"
            >
              <option *ngFor="let l of languages" [value]="l.code">{{ l.name }}</option>
            </select>

            <button
              type="button"
              class="st-mic"
              [class.is-recording]="isRecording"
              [disabled]="!speechSupported || sendingToAI"
              (click)="toggleDictation()"
              [attr.aria-label]="isRecording ? 'Stop recording' : 'Start recording'"
              [matTooltip]="isRecording ? 'Stop recording' : 'Start recording'"
              matTooltipPosition="above"
            >
              <img *ngIf="!isRecording" src="assets/icons/mic.svg" alt="" width="22" height="22" />
              <img *ngIf="isRecording" src="assets/icons/micx.svg" alt="" width="22" height="22" />
            </button>

            <span class="st-status" *ngIf="isRecording"><span class="st-dot"></span>Recording… speak now</span>
            <span class="st-status st-status--muted" *ngIf="!isRecording && stoppedForSilence">
              Stopped after a minute with no speech. Press the microphone to continue.
            </span>
            <span class="st-status st-status--muted" *ngIf="!isRecording && !stoppedForSilence && transcript">
              Press the microphone to add to the transcript.
            </span>

            <button
              type="button"
              class="st-link st-clear"
              *ngIf="transcript && !sendingToAI"
              (click)="clearTranscript()"
            >
              Clear dictated text
            </button>
          </div>

          <label class="st-label" for="st-transcript">Transcript</label>
          <textarea
            id="st-transcript"
            class="st-textarea st-transcript"
            [(ngModel)]="transcript"
            (ngModelChange)="saveDraft()"
            [disabled]="sendingToAI"
            placeholder="Press the microphone and speak. The dialog with the patient will appear here, and you can correct it before sending."
          ></textarea>
          <div class="st-interim" *ngIf="interimTranscript">{{ interimTranscript }}</div>
          <!-- whether this browser's recognizer accepted the English medical terms (aweb-api prompts/medical_vocabulary.txt) -->
          <div class="st-hint" *ngIf="vocabularyState == 'active'">Medical vocabulary: on</div>
          <div class="st-hint" *ngIf="vocabularyState == 'unsupported'">
            Medical vocabulary is not supported by this browser, so English medical terms may be missed.
          </div>
        </section>

        <!-- 2. send to AI -->
        <section class="st-section">
          <label class="st-label" for="st-instructions">
            Special instructions for the AI <span class="st-optional">(optional)</span>
          </label>
          <textarea
            id="st-instructions"
            class="st-textarea st-instructions"
            [(ngModel)]="instructions"
            [disabled]="sendingToAI"
            placeholder="For example: write the consultation in English, keep it short, list the recommendations as bullets."
          ></textarea>

          <!-- only when the server allows filling the whole exam; otherwise the AI fills only the Consultation -->
          <div class="st-scope" role="radiogroup" aria-label="What the AI fills in" *ngIf="fullExamAllowed">
            <span class="st-label">The AI fills in:</span>
            <label>
              <input type="radio" name="st-scope" value="consultation" [(ngModel)]="scope" [disabled]="sendingToAI" />
              Only the Consultation field
            </label>
            <label>
              <input type="radio" name="st-scope" value="full" [(ngModel)]="scope" [disabled]="sendingToAI" />
              The whole exam (also pregnancy page, anatomy, placenta and summary)
            </label>
          </div>

          <div class="st-actions">
            <button
              type="button"
              class="search-btn st-send"
              [disabled]="!transcript.trim() || sendingToAI"
              (click)="sendToAI()"
            >
              {{ hasResults ? 'Send again to AI' : 'Send to AI' }}
            </button>
            <!-- no choice to make when the server allows a single AI -->
            <label class="st-using" *ngIf="aiSystems.length > 1">
              Using:
              <select class="t-select" [(ngModel)]="AIsystem" [disabled]="sendingToAI">
                <option *ngFor="let s of aiSystems" [value]="s.value">{{ s.name }}</option>
              </select>
            </label>
            <span class="st-hint" *ngIf="hasResults && !sendingToAI">
              Sending again processes the whole transcript and replaces the AI results in the exam.
            </span>
          </div>

          <div class="st-waiting" *ngIf="sendingToAI">
            <div class="st-spinner"></div>
            <span>Waiting for AI… {{ aiElapsedSeconds }}s</span>
          </div>
          <div class="st-message st-message--error" *ngIf="aiError">{{ aiError }}</div>
        </section>

        <!-- 3. results + change requests -->
        <section class="st-section st-results" *ngIf="hasResults && !sendingToAI">
          <h3>Results saved to the exam</h3>
          <p class="st-hint" *ngIf="aiElapsedSeconds > 0">AI responded in {{ aiElapsedSeconds }}s.</p>

          <ng-container *ngIf="consultationHtml">
            <div class="st-label">Consultation</div>
            <div class="st-consultation" [attr.dir]="consultationDir()" [innerHTML]="consultationHtml"></div>
          </ng-container>

          <details class="st-changes" *ngIf="changes.length">
            <summary>Other exam fields updated ({{ changes.length }})</summary>
            <table>
              <tr *ngFor="let c of changes">
                <td class="st-changes__section">{{ c.section }}</td>
                <td class="st-changes__field">{{ c.field }}</td>
                <td>{{ c.value }}</td>
              </tr>
            </table>
          </details>

          <details class="st-changes" *ngIf="unprocessed.length">
            <summary>Text the AI could not place ({{ unprocessed.length }})</summary>
            <ul>
              <li *ngFor="let u of unprocessed">{{ u }}</li>
            </ul>
          </details>

          <label class="st-label" for="st-change-request">Ask the AI for changes</label>
          <textarea
            id="st-change-request"
            class="st-textarea st-instructions"
            [(ngModel)]="changeRequest"
            placeholder="For example: the patient is G3 P2, not G2 P1. Add that a follow-up scan was scheduled in 4 weeks."
          ></textarea>
          <div class="st-actions">
            <button
              type="button"
              class="search-btn st-send"
              [disabled]="!changeRequest.trim()"
              (click)="requestChanges()"
            >
              Apply changes
            </button>
            <span class="st-hint">You can also edit the Consultation field directly in the pregnancy tab.</span>
          </div>

          <details class="st-changes" *ngIf="history.length > 1">
            <summary>Request history ({{ history.length }})</summary>
            <ul>
              <li *ngFor="let h of history">
                <strong>{{ h.time | date: 'dd/MM/yy HH:mm' }} – {{ h.type }}</strong>
                <span *ngIf="h.text">: {{ h.text }}</span>
              </li>
            </ul>
          </details>
        </section>
      </ng-container>
    </div>
  </div>
</div>
<!-- ===================================== POPUP : DICTATION / AI (END)  =============================================== -->
